import { useQuery } from "@tanstack/react-query"
import api from "../../http"

export interface BindingUserPaginatedResponse<T> {
    docs: T[];
    totalDocs: number;
    limit: number;
    totalPages: number;
    page: number;
    hasPrevPage: boolean;
    hasNextPage: boolean;
    prevPage: number | null;
    nextPage: number | null;
    ownerName?: string;
}

export interface FetchBindingUsersParams {
    bindingType: number;
    forBinding?: string;
    page?: number;
    limit?: number;
    userNameOrEmail?: string;
}

export const fetchBindingUsers = async <T = unknown>({
    bindingType,
    forBinding,
    page = 1,
    limit = 10,
    userNameOrEmail,
}: FetchBindingUsersParams) => {
    const { data } = await api.get<BindingUserPaginatedResponse<T>>(`/binding-users/findAll/${bindingType}`, {
        params: { forBinding, limit, page, userNameOrEmail }
    })
    return data
}

interface UseGetBindingUserParams {
    bindingType: number;
    forBinding?: string;
    page?: number;
    limit?: number;
    userNameOrEmail?: string;
    enabled?: boolean;
}

export const useGetBindingUser = <T = unknown>({ bindingType, forBinding, page = 1, limit = 10, userNameOrEmail, enabled = true }: UseGetBindingUserParams) => {
    const query = useQuery<BindingUserPaginatedResponse<T>>({
        queryKey: ['binding-users', bindingType, forBinding, page, limit, userNameOrEmail],
        enabled,
        queryFn: () => fetchBindingUsers<T>({ bindingType, forBinding, page, limit, userNameOrEmail })
    });

    return {
        ...query,
        data: query.data?.docs ?? [],
        pagination: query.data ? {
            totalDocs: query.data.totalDocs,
            totalPages: query.data.totalPages,
            page: query.data.page,
            hasPrevPage: query.data.hasPrevPage,
            hasNextPage: query.data.hasNextPage,
            prevPage: query.data.prevPage,
            nextPage: query.data.nextPage,
        } : null,
    };
}
